/**
 * Processing panel — the per-document pipeline timeline (Ale 2026-08-03).
 *
 * Renders GET /documents/{id}/pipeline-timeline as PHASE rows on a split
 * time axis. Spans collapse into seven phases (read → extract → filter &
 * review → build → officer → write → checks) so the panel reads at a
 * glance, and the axis breaks into a machine segment and a review segment —
 * officer review dominates real timelines by an order of magnitude, and on
 * a single proportional axis it crushed every pipeline bar into slivers
 * (Ale 2026-08-03). Colors: LLM amber, deterministic grey, officer blue,
 * record write green, evidence checks outlined green. Polls every 3s while
 * the document is still moving.
 */
import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../lib/api'

interface Span {
  label: string
  kind: 'llm' | 'det' | 'officer' | 'write' | 'evidence' | string
  started_at: string | null
  ended_at: string | null
  detail: string | null
}

interface Timeline {
  document_id: string
  filename: string
  status: string
  uploaded_at: string | null
  spans: Span[]
}

const LIVE_STATUSES = new Set(['uploaded', 'processing', 'splitting', 'classified'])

const KIND_STYLE: Record<string, { background?: string; border?: string }> = {
  llm: { background: '#e8b84b' },
  det: { background: '#9aa3b8' },
  officer: { background: 'var(--blue)' },
  write: { background: 'var(--accent-ink)' },
  evidence: { background: 'transparent', border: '2px solid var(--accent-ink)' },
}

// Phase buckets, in pipeline order. `review` phases sit on the second axis
// segment — everything after the machine hands over to the officer. The
// "Build & map evidence" matcher is the catch-all: ordering below decides
// ownership (first match wins).
const PHASES: { name: string; kind: string; review: boolean; match: (s: Span) => boolean }[] = [
  {
    name: 'Read & classify',
    kind: 'llm',
    review: false,
    match: (s) => /^(Classifier|Splitter)/.test(s.label),
  },
  { name: 'Extract', kind: 'llm', review: false, match: (s) => s.label.startsWith('Extract') },
  {
    name: 'Filter & peer review',
    kind: 'llm',
    review: false,
    match: (s) => /^(Filter|Peer review)/.test(s.label),
  },
  { name: 'Officer review', kind: 'officer', review: true, match: (s) => s.kind === 'officer' },
  { name: 'Record write', kind: 'write', review: true, match: (s) => s.kind === 'write' },
  {
    name: 'Obligation checks',
    kind: 'evidence',
    review: true,
    match: (s) => s.label.startsWith('Obligation'),
  },
  { name: 'Build & map evidence', kind: 'det', review: false, match: () => true },
]

// Render order: machine phases first, then the review side.
const PHASE_ORDER = [
  'Read & classify',
  'Extract',
  'Filter & peer review',
  'Build & map evidence',
  'Officer review',
  'Record write',
  'Obligation checks',
]

interface PhaseRow {
  name: string
  kind: string
  review: boolean
  start: number
  end: number | null // null = still open
  calls: number
  detail: string | null
}

/** A duration in officer words (K7-style, 2026-09-03): "7.2 s",
 *  "4 min 57 s", "23 h 41 min", "2 d 5 h" — never "+1420m 41s". */
function fmtDur(ms: number): string {
  const s = Math.max(0, ms) / 1000
  if (s < 60) return `${s.toFixed(1)} s`
  const totalMin = Math.floor(s / 60)
  if (totalMin < 60) return `${totalMin} min ${Math.round(s - totalMin * 60)} s`
  const totalH = Math.floor(totalMin / 60)
  if (totalH < 24) return `${totalH} h ${totalMin - totalH * 60} min`
  const d = Math.floor(totalH / 24)
  return `${d} d ${totalH - d * 24} h`
}

export default function PipelinePanel({ documentId }: { documentId: string }) {
  const [tl, setTl] = useState<Timeline | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const timer = useRef<number | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const t = await apiFetch<Timeline>(
          `/api/documents/${encodeURIComponent(documentId)}/pipeline-timeline`
        )
        if (cancelled) return
        setTl(t)
        setNow(Date.now())
        const officerOpen = t.spans.some((s) => s.kind === 'officer' && !s.ended_at)
        if (LIVE_STATUSES.has(t.status) || officerOpen) {
          timer.current = window.setTimeout(load, 3000)
        }
      } catch {
        // The panel is additive — a failed load renders nothing, never an
        // error wall over the document page.
        if (!cancelled) setTl(null)
      }
    }
    load()
    return () => {
      cancelled = true
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [documentId])

  if (!tl || tl.spans.length === 0) return null

  // ── Collapse spans into phase rows ─────────────────────────────────────
  const byPhase = new Map<string, PhaseRow>()
  // Busy time per side, summed over the RAW spans — a phase row's merged
  // span stretches across officer wait (late evidence writes land days
  // after upload), so the row span is wall-clock, never work done.
  let machineBusy = 0
  let reviewBusy = 0
  for (const s of tl.spans) {
    const phase = PHASES.find((p) => p.match(s))
    if (!phase || !s.started_at) continue
    const st = Date.parse(s.started_at)
    if (s.ended_at) {
      const d = Math.max(0, Date.parse(s.ended_at) - st)
      if (phase.review) reviewBusy += d
      else machineBusy += d
    }
    const row = byPhase.get(phase.name)
    if (!row) {
      byPhase.set(phase.name, {
        name: phase.name,
        kind: phase.kind,
        review: phase.review,
        start: st,
        end: s.ended_at ? Date.parse(s.ended_at) : null,
        calls: 1,
        detail: s.detail,
      })
      continue
    }
    row.calls += 1
    row.start = Math.min(row.start, st)
    if (row.end !== null) {
      row.end = s.ended_at ? Math.max(row.end, Date.parse(s.ended_at)) : null
    }
  }
  const rows = PHASE_ORDER.map((n) => byPhase.get(n)).filter((r): r is PhaseRow => r != null)
  if (rows.length === 0) return null

  // ── Split axis: machine segment | review segment ───────────────────────
  const uploaded = tl.uploaded_at ? Date.parse(tl.uploaded_at) : NaN
  const t0 = Math.min(...rows.map((r) => r.start), ...(Number.isNaN(uploaded) ? [] : [uploaded]))
  const rowEnd = (r: PhaseRow) => r.end ?? now
  const machineRows = rows.filter((r) => !r.review)
  const reviewRows = rows.filter((r) => r.review)
  const machineEnd =
    machineRows.length > 0 ? Math.max(...machineRows.map(rowEnd), t0 + 1000) : t0 + 1000
  const t1 = Math.max(...rows.map(rowEnd), machineEnd + 1000)
  const hasReview = reviewRows.length > 0
  // Machine gets 58% of the width, review the right 38%; the gap is the break.
  const MACHINE_W = hasReview ? 58 : 100
  const REVIEW_X = 62
  const xPct = (t: number): number => {
    if (t <= machineEnd || !hasReview) return ((t - t0) / (machineEnd - t0)) * MACHINE_W
    return REVIEW_X + ((t - machineEnd) / Math.max(t1 - machineEnd, 1000)) * (100 - REVIEW_X)
  }

  const live = LIVE_STATUSES.has(tl.status)
  /* The headline numbers are BUSY time (summed raw-span durations), not
   * the wall-clock split: the span read "machine 9561m" on a two-minute
   * pipeline once late evidence writes landed days after upload
   * (real-browser walk find, 2026-08-31). The axis below stays
   * wall-clock — that is its job. */
  const machineDur = machineBusy > 0 ? machineBusy : machineEnd - t0
  const reviewDur = reviewBusy > 0 ? reviewBusy : t1 - machineEnd

  return (
    <div className="vcard" style={{ padding: '12px 16px' }} data-testid="pipeline-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Processing</div>
        <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
          {live && 'live — updating · '}
          machine {fmtDur(machineDur)}
          {hasReview && <> · review {fmtDur(reviewDur)}</>}
        </div>
      </div>
      {hasReview && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '128px 1fr',
            fontSize: 'var(--text-12)',
            color: 'var(--faint)',
            textTransform: 'uppercase',
            letterSpacing: '.06em',
            marginBottom: 2,
          }}
        >
          <span />
          <span style={{ position: 'relative', display: 'block', height: 12 }}>
            <span style={{ position: 'absolute', left: 0 }}>machine · {fmtDur(machineDur)}</span>
            <span style={{ position: 'absolute', left: `${REVIEW_X}%` }}>
              officer · {fmtDur(reviewDur)}
            </span>
          </span>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {rows.map((r) => {
          const en = rowEnd(r)
          const left = Math.max(0, xPct(r.start))
          const width = Math.max(1.2, xPct(en) - left)
          const style = KIND_STYLE[r.kind] ?? KIND_STYLE.llm
          return (
            <div
              key={r.name}
              style={{ display: 'grid', gridTemplateColumns: '128px 1fr', alignItems: 'center' }}
            >
              <div
                style={{
                  fontSize: 'var(--text-12)',
                  color: 'var(--muted)',
                  paddingRight: 8,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {r.name}
              </div>
              <div
                style={{ position: 'relative', height: 16, borderLeft: '1px solid var(--line)' }}
              >
                {hasReview && (
                  <span
                    style={{
                      position: 'absolute',
                      left: '59.5%',
                      top: 0,
                      bottom: 0,
                      borderLeft: '1px dashed var(--line)',
                    }}
                  />
                )}
                <div
                  title={`${r.name} · ${r.calls} step${r.calls === 1 ? '' : 's'} · ${fmtDur(en - r.start)}`}
                  style={{
                    position: 'absolute',
                    top: 3,
                    height: 10,
                    borderRadius: 3,
                    left: `${left}%`,
                    width: `${Math.min(width, 100 - left)}%`,
                    opacity: r.end === null ? 0.55 : 1,
                    boxSizing: 'border-box',
                    ...style,
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          marginTop: 8,
          fontSize: 'var(--text-12)',
          color: 'var(--muted)',
        }}
      >
        {(
          [
            ['llm', 'AI call'],
            ['det', 'deterministic'],
            ['officer', 'officer'],
            ['write', 'record write'],
            ['evidence', 'evidence check'],
          ] as const
        ).map(([kind, label]) => (
          <span key={kind} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: 2,
                display: 'inline-block',
                boxSizing: 'border-box',
                ...KIND_STYLE[kind],
              }}
            />
            {label}
          </span>
        ))}
      </div>
      <div style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
        {rows.map((r) => (
          <div
            key={`ev-${r.name}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '108px 1fr',
              gap: 8,
              fontSize: 'var(--text-12)',
              lineHeight: '18px',
            }}
          >
            <span
              style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}
              data-testid="pipeline-offset"
            >
              {r.start - t0 < 500 ? 'at the start' : `after ${fmtDur(r.start - t0)}`}
            </span>
            <span>
              <strong style={{ fontWeight: 600 }}>{r.name}</strong>
              <span style={{ color: 'var(--muted)' }}>
                {r.calls > 1 ? ` — ${r.calls} steps` : ''}
                {r.detail && r.calls === 1 ? ` — ${r.detail}` : ''}
                {r.end !== null ? ` · ${fmtDur(rowEnd(r) - r.start)}` : ''}
              </span>
              {r.end === null && <span style={{ color: 'var(--blue)' }}> · open</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
